import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import {
  AdminPanelService,
  BatchTestJob,
  BatchTestRow,
  ChatRecording,
  AdminProduct,
  AdminSummary,
  AnythingLlmDocument,
  AnythingLlmDocumentsResponse,
  MissingQuestion,
  ProductListResponse,
} from './admin-panel.service';

type AdminTab = 'products' | 'assets' | 'documents' | 'test' | 'recordings' | 'missing';

@Component({
  selector: 'app-admin-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './admin-panel.html',
  styleUrl: './admin-panel.css',
})
export class AdminPanel implements OnInit, OnDestroy {
  activeTab: AdminTab = 'products';
  summary?: AdminSummary;
  products: AdminProduct[] = [];
  selectedProduct?: AdminProduct;
  categories: string[] = [];
  seriesByCategory: Record<string, string[]> = {};
  missingQuestions: MissingQuestion[] = [];
  chatRecordings: ChatRecording[] = [];
  workspaceDocuments: AnythingLlmDocument[] = [];
  localDocuments: AnythingLlmDocument[] = [];
  selectedDocumentPaths: Record<string, boolean> = {};
  documentSearch = '';
  workspaceName = '';
  chatRecordingPath = '';
  isChatRecordingEnabled = false;
  chatRecordingCount = 0;
  filteredChatRecordingCount = 0;
  chatRecordingStartDate = '';
  chatRecordingEndDate = '';
  search = '';
  category = '';
  series = '';
  totalProducts = 0;
  productLimit = 40;
  productOffset = 0;
  applicationsText = '';
  testQuestion = 'wpc wall panel series woodgrain apa aja produk nya';
  testSessionId = `admin-${Date.now()}`;
  testResponse = '';
  batchTestJob?: BatchTestJob | null;
  batchTestRows: BatchTestRow[] = [];
  batchTestFileName = '';
  batchTestDelayMs = 400;
  batchTestUseSameSession = false;
  batchTestProgress = 0;
  isLoadingProducts = false;
  isSaving = false;
  isTesting = false;
  isUploadingBatchTest = false;
  isBatchTesting = false;
  isLoadingDocuments = false;
  isUploadingDocument = false;
  isSyncingDocuments = false;
  isSyncingFaq = false;
  isSyncingAreas = false;
  isLoadingRecordings = false;
  isTogglingRecording = false;
  isClearingRecordings = false;
  statusMessage = '';
  private isDestroyed = false;
  private batchTestPollId?: number;
  private batchTestRevision = 0;

  constructor(
    private adminService: AdminPanelService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.refreshAll();
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    this.stopBatchTestPolling();
  }

  get currentSeriesOptions(): string[] {
    if (!this.category) {
      return [];
    }

    return this.seriesByCategory[this.category] || [];
  }

  get canGoPrevious(): boolean {
    return this.productOffset > 0;
  }

  get canGoNext(): boolean {
    return this.productOffset + this.productLimit < this.totalProducts;
  }

  refreshAll() {
    this.loadSummary();
    this.loadProducts();
    this.loadDocuments();
    this.loadMissingQuestions();
    this.loadChatRecording();
    this.loadBatchTest(false, false);
  }

  setTab(tab: AdminTab) {
    this.activeTab = tab;
  }

  loadSummary() {
    this.adminService.getSummary().subscribe({
      next: (summary) => (this.summary = summary),
      error: () => this.setStatus('Gagal memuat summary admin.'),
    });
  }

  loadProducts(resetOffset = false) {
    if (resetOffset) {
      this.productOffset = 0;
    }

    this.isLoadingProducts = true;
    this.adminService
      .getProducts({
        search: this.search,
        category: this.category,
        series: this.series,
        limit: this.productLimit,
        offset: this.productOffset,
      })
      .subscribe({
        next: (response) => this.applyProductResponse(response),
        error: () => {
          this.isLoadingProducts = false;
          this.setStatus('Gagal memuat produk.');
        },
      });
  }

  applyProductResponse(response: ProductListResponse) {
    this.products = response.products;
    this.totalProducts = response.total;
    this.categories = response.categories;
    this.seriesByCategory = response.seriesByCategory;
    this.isLoadingProducts = false;

    if (!this.selectedProduct && this.products.length) {
      this.selectProduct(this.products[0]);
    }
  }

  selectProduct(product: AdminProduct) {
    this.selectedProduct = {
      ...product,
      applications: [...(product.applications || [])],
    };
    this.applicationsText = this.selectedProduct.applications.join(', ');
  }

  saveProduct() {
    if (!this.selectedProduct) {
      return;
    }

    this.isSaving = true;
    this.selectedProduct.applications = this.applicationsText
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    this.adminService.updateProduct(this.selectedProduct).subscribe({
      next: () => {
        this.isSaving = false;
        this.setStatus('Produk berhasil disimpan.');
        this.loadSummary();
        this.loadProducts();
      },
      error: () => {
        this.isSaving = false;
        this.setStatus('Gagal menyimpan produk.');
      },
    });
  }

  saveAssets() {
    if (!this.selectedProduct) {
      return;
    }

    this.isSaving = true;
    this.adminService.updateProductAssets(this.selectedProduct).subscribe({
      next: (response) => {
        this.isSaving = false;
        this.selectProduct(response.product);
        this.setStatus('Asset produk berhasil disimpan.');
        this.loadSummary();
        this.loadProducts();
      },
      error: () => {
        this.isSaving = false;
        this.setStatus('Gagal menyimpan asset produk.');
      },
    });
  }

  previousPage() {
    if (!this.canGoPrevious) {
      return;
    }

    this.productOffset = Math.max(this.productOffset - this.productLimit, 0);
    this.loadProducts();
  }

  nextPage() {
    if (!this.canGoNext) {
      return;
    }

    this.productOffset += this.productLimit;
    this.loadProducts();
  }

  runChatTest() {
    const question = this.testQuestion.trim();

    if (!question) {
      return;
    }

    this.isTesting = true;
    this.testResponse = '';
    this.adminService.testChat(question, this.testSessionId).subscribe({
      next: (response) => {
        this.isTesting = false;
        this.testResponse = response.message;
      },
      error: () => {
        this.isTesting = false;
        this.testResponse = 'Gagal test chat.';
      },
    });
  }

  uploadBatchTestFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const lowerName = file.name.toLowerCase();

    if (lowerName.endsWith('.xlsx')) {
      this.setStatus('File .xlsx native belum didukung di batch test ini. Export/save as .xls, .csv, atau .tsv dulu ya.');
      input.value = '';
      return;
    }

    this.isUploadingBatchTest = true;
    this.setStatus(`Upload ${file.name} sedang diproses.`);

    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;

      this.adminService
        .uploadBatchTest({
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          base64,
          delayMs: this.batchTestDelayMs,
          useSameSession: this.batchTestUseSameSession,
        })
        .subscribe({
          next: (response) => {
            this.isUploadingBatchTest = false;
            this.applyBatchTestResponse(response.job);
            this.setStatus(`${response.job?.total || 0} pertanyaan mulai diproses dari ${file.name}.`);
            window.setTimeout(() => this.loadBatchTest(true), 250);
            input.value = '';
          },
          error: (error) => {
            this.isUploadingBatchTest = false;
            this.applyBatchTestResponse(null);
            this.setStatus(error?.error?.message || 'Gagal upload file batch test.');
            input.value = '';
          },
        });
    };

    reader.onerror = () => {
      this.isUploadingBatchTest = false;
      this.setStatus('Gagal membaca file batch test.');
      this.requestViewUpdate();
      input.value = '';
    };

    reader.readAsDataURL(file);
  }

  runBatchTest() {
    if (!this.batchTestJob || this.isBatchTesting) {
      return;
    }

    this.adminService
      .startBatchTest(this.batchTestJob.id, {
        delayMs: this.batchTestDelayMs,
        useSameSession: this.batchTestUseSameSession,
      })
      .subscribe({
        next: (response) => {
          this.applyBatchTestResponse(response.job);
          this.setStatus('Batch test dijalankan lagi.');
          window.setTimeout(() => this.loadBatchTest(true), 250);
        },
        error: () => this.setStatus('Gagal menjalankan batch test.'),
      });
  }

  stopBatchTest() {
    if (!this.batchTestJob) {
      return;
    }

    this.adminService.stopBatchTest(this.batchTestJob.id).subscribe({
      next: (response) => {
        this.applyBatchTestResponse(response.job);
        this.setStatus('Batch test dihentikan.');
      },
      error: () => this.setStatus('Gagal menghentikan batch test.'),
    });
  }

  clearBatchTest() {
    if (this.isBatchTesting) {
      return;
    }

    this.adminService.clearBatchTest().subscribe({
      next: (response) => {
        this.applyBatchTestResponse(response.job);
        this.setStatus('Batch test dibersihkan.');
      },
      error: () => this.setStatus('Gagal membersihkan batch test.'),
    });
  }

  exportBatchTestResults() {
    if (!this.batchTestRows.length) {
      this.setStatus('Belum ada hasil batch test untuk diexport.');
      return;
    }

    window.location.href = this.adminService.getBatchTestExportUrl();
  }

  get completedBatchTestCount() {
    return this.batchTestJob?.completed ?? this.batchTestRows.filter((row) => ['done', 'error', 'skipped'].includes(row.status)).length;
  }

  loadBatchTest(silent = false, delta = true) {
    const sinceRevision = delta && this.batchTestRows.length ? this.batchTestRevision : undefined;

    this.adminService.getBatchTest(sinceRevision).subscribe({
      next: (response) => this.applyBatchTestResponse(response.job),
      error: () => {
        if (!silent) {
          this.setStatus('Gagal memuat batch test.');
        }
        this.requestViewUpdate();
      },
    });
  }

  applyBatchTestResponse(job?: BatchTestJob | null) {
    if (!job) {
      this.batchTestJob = null;
      this.batchTestRows = [];
      this.batchTestFileName = '';
      this.batchTestProgress = 0;
      this.batchTestRevision = 0;
      this.isBatchTesting = false;
      this.stopBatchTestPolling();
      this.requestViewUpdate();
      return;
    }

    const canMergeDelta = job.fullRows === false && this.batchTestJob?.id === job.id && this.batchTestRows.length > 0;
    const rows = canMergeDelta ? this.mergeBatchTestRows(this.batchTestRows, job.rows || []) : job.rows || [];

    this.batchTestJob = {
      ...job,
      rows,
    };
    this.batchTestRows = rows;
    this.batchTestFileName = job?.filename || '';
    this.batchTestProgress = job?.progressPercent || 0;
    this.batchTestRevision = Math.max(this.batchTestRevision, Number(job.revision || 0));

    this.batchTestDelayMs = job.delayMs;
    this.batchTestUseSameSession = job.useSameSession;

    this.isBatchTesting = job.status === 'running';

    if (this.isBatchTesting) {
      this.startBatchTestPolling();
    } else {
      this.stopBatchTestPolling();
    }

    this.requestViewUpdate();
  }

  trackBatchTestRow(_index: number, row: BatchTestRow) {
    return row.rowNumber;
  }

  private mergeBatchTestRows(currentRows: BatchTestRow[], changedRows: BatchTestRow[]) {
    if (!changedRows.length) {
      return currentRows;
    }

    const changedByNumber = new Map(changedRows.map((row) => [row.rowNumber, row]));
    const mergedRows = currentRows.map((row) => changedByNumber.get(row.rowNumber) || row);
    const knownRows = new Set(mergedRows.map((row) => row.rowNumber));
    const newRows = changedRows.filter((row) => !knownRows.has(row.rowNumber));

    return newRows.length ? [...mergedRows, ...newRows].sort((a, b) => a.rowNumber - b.rowNumber) : mergedRows;
  }

  loadMissingQuestions() {
    this.adminService.getMissingQuestions().subscribe({
      next: (response) => (this.missingQuestions = response.questions),
      error: () => this.setStatus('Gagal memuat missing questions.'),
    });
  }

  clearMissingQuestions() {
    this.adminService.clearMissingQuestions().subscribe({
      next: (response) => {
        this.missingQuestions = response.questions;
        this.loadSummary();
      },
      error: () => this.setStatus('Gagal membersihkan missing questions.'),
    });
  }

  loadChatRecording() {
    this.isLoadingRecordings = true;
    this.adminService
      .getChatRecording(this.getChatRecordingFilters())
      .pipe(finalize(() => (this.isLoadingRecordings = false)))
      .subscribe({
        next: (response) => this.applyChatRecordingResponse(response),
        error: () => {
          this.setStatus('Gagal memuat chat recording.');
        },
      });
  }

  toggleChatRecording() {
    this.isTogglingRecording = true;
    this.adminService.setChatRecording(!this.isChatRecordingEnabled).subscribe({
      next: (response) => {
        this.applyChatRecordingResponse(response);
        this.isTogglingRecording = false;
        this.setStatus(response.enabled ? 'Recording chat dinyalakan.' : 'Recording chat dimatikan.');
        this.loadSummary();
      },
      error: () => {
        this.isTogglingRecording = false;
        this.setStatus('Gagal mengubah status recording.');
      },
    });
  }

  clearChatRecordings() {
    this.isClearingRecordings = true;
    this.adminService.clearChatRecording().subscribe({
      next: (response) => {
        this.applyChatRecordingResponse(response);
        this.isClearingRecordings = false;
        this.setStatus('Chat recordings berhasil dibersihkan.');
        this.loadSummary();
      },
      error: () => {
        this.isClearingRecordings = false;
        this.setStatus('Gagal membersihkan chat recordings.');
      },
    });
  }

  filterChatRecordings() {
    this.loadChatRecording();
  }

  get hasChatRecordingDateFilter(): boolean {
    return Boolean(this.chatRecordingStartDate || this.chatRecordingEndDate);
  }

  resetChatRecordingFilter() {
    this.chatRecordingStartDate = '';
    this.chatRecordingEndDate = '';
    this.loadChatRecording();
  }

  useChatRecordingShortcut(days: number) {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - Math.max(days - 1, 0));

    this.chatRecordingStartDate = this.toDateInputValue(startDate);
    this.chatRecordingEndDate = this.toDateInputValue(today);
    this.loadChatRecording();
  }

  isChatRecordingShortcutActive(days: number) {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - Math.max(days - 1, 0));

    return (
      this.chatRecordingStartDate === this.toDateInputValue(startDate) &&
      this.chatRecordingEndDate === this.toDateInputValue(today)
    );
  }

  exportChatRecordings() {
    if (this.filteredChatRecordingCount === 0) {
      this.setStatus('Tidak ada chat logs untuk diexport.');
      return;
    }

    window.location.href = this.adminService.getChatRecordingExportUrl(this.getChatRecordingFilters());
  }

  applyChatRecordingResponse(response: {
    enabled: boolean;
    count: number;
    recordings: ChatRecording[];
    path: string;
    filteredCount?: number;
  }) {
    this.isChatRecordingEnabled = response.enabled;
    this.chatRecordingCount = response.count;
    this.filteredChatRecordingCount = response.filteredCount ?? response.recordings.length;
    this.chatRecordings = response.recordings;
    this.chatRecordingPath = response.path;
    this.isLoadingRecordings = false;
  }

  get filteredLocalDocuments(): AnythingLlmDocument[] {
    const search = this.documentSearch.trim().toLowerCase();

    if (!search) {
      return this.localDocuments;
    }

    return this.localDocuments.filter((document) =>
      [document.title, document.name, document.docpath, document.folder]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search))
    );
  }

  get selectedDocumentCount(): number {
    return Object.values(this.selectedDocumentPaths).filter(Boolean).length;
  }

  loadDocuments() {
    this.isLoadingDocuments = true;
    this.adminService.getDocuments().subscribe({
      next: (response) => this.applyDocumentsResponse(response),
      error: () => {
        this.isLoadingDocuments = false;
        this.setStatus('Gagal memuat dokumen AnythingLLM.');
      },
    });
  }

  applyDocumentsResponse(response: AnythingLlmDocumentsResponse) {
    this.workspaceDocuments = response.workspaceDocuments;
    this.localDocuments = response.localDocuments;
    this.workspaceName = response.workspace.name || response.workspace.slug;
    this.isLoadingDocuments = false;
    this.isUploadingDocument = false;
    this.isSyncingDocuments = false;
    this.selectedDocumentPaths = {};
    this.loadSummary();
  }

  toggleDocumentSelection(document: AnythingLlmDocument, checked: boolean) {
    this.selectedDocumentPaths[document.docpath] = checked;
  }

  syncSelectedDocuments() {
    const adds = Object.entries(this.selectedDocumentPaths)
      .filter(([_path, checked]) => checked)
      .map(([path]) => path);

    if (!adds.length) {
      this.setStatus('Pilih dokumen dulu untuk sync.');
      return;
    }

    this.isSyncingDocuments = true;
    this.adminService.syncWorkspaceDocuments({ adds }).subscribe({
      next: (response) => {
        this.applyDocumentsResponse(response);
        this.setStatus('Dokumen berhasil disinkronkan ke workspace.');
      },
      error: () => {
        this.isSyncingDocuments = false;
        this.setStatus('Gagal sync dokumen ke workspace.');
      },
    });
  }

  syncFaqKnowledge() {
    this.isSyncingFaq = true;
    this.adminService.syncFaqKnowledge().subscribe({
      next: (response) => {
        this.isSyncingFaq = false;
        this.setStatus(`FAQ berhasil disinkronkan: ${response.faqCount} pertanyaan aktif.`);
        this.loadSummary();
      },
      error: () => {
        this.isSyncingFaq = false;
        this.setStatus('Gagal sync FAQ dari Excel.');
      },
    });
  }

  syncIndonesiaAreas() {
    this.isSyncingAreas = true;
    this.setStatus('Sedang sync data kecamatan/kelurahan Indonesia. Ini bisa beberapa menit.');
    this.adminService.syncIndonesiaAreas().subscribe({
      next: (response) => {
        this.isSyncingAreas = false;
        this.setStatus(`Data area berhasil disinkronkan: ${response.areaCount} lokasi aktif.`);
        this.loadSummary();
      },
      error: () => {
        this.isSyncingAreas = false;
        this.setStatus('Gagal sync data area Indonesia.');
      },
    });
  }

  removeDocumentFromWorkspace(document: AnythingLlmDocument) {
    if (!document.docpath) {
      return;
    }

    this.isSyncingDocuments = true;
    this.adminService.syncWorkspaceDocuments({ deletes: [document.docpath] }).subscribe({
      next: (response) => {
        this.applyDocumentsResponse(response);
        this.setStatus('Dokumen dilepas dari workspace.');
      },
      error: () => {
        this.isSyncingDocuments = false;
        this.setStatus('Gagal melepas dokumen dari workspace.');
      },
    });
  }

  uploadDocument(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    this.isUploadingDocument = true;
    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',')[1] : result;

      this.adminService
        .uploadDocument({
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          base64,
        })
        .subscribe({
          next: (response) => {
            this.applyDocumentsResponse(response);
            this.setStatus(`${file.name} berhasil diupload dan disinkronkan.`);
            input.value = '';
          },
          error: () => {
            this.isUploadingDocument = false;
            this.setStatus('Gagal upload dokumen.');
            input.value = '';
          },
        });
    };

    reader.onerror = () => {
      this.isUploadingDocument = false;
      this.setStatus('Gagal membaca file upload.');
      input.value = '';
    };

    reader.readAsDataURL(file);
  }

  private startBatchTestPolling() {
    if (this.batchTestPollId) {
      return;
    }

    this.batchTestPollId = window.setInterval(() => this.loadBatchTest(true), 750);
  }

  private stopBatchTestPolling() {
    if (!this.batchTestPollId) {
      return;
    }

    window.clearInterval(this.batchTestPollId);
    this.batchTestPollId = undefined;
  }

  private setStatus(message: string) {
    this.statusMessage = message;
    this.requestViewUpdate();
    window.setTimeout(() => {
      if (this.statusMessage === message) {
        this.statusMessage = '';
        this.requestViewUpdate();
      }
    }, 3200);
  }

  private requestViewUpdate() {
    if (this.isDestroyed) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (!this.isDestroyed) {
        this.changeDetector.detectChanges();
      }
    });
  }

  private getChatRecordingFilters() {
    return {
      startDate: this.chatRecordingStartDate,
      endDate: this.chatRecordingEndDate,
    };
  }

  private toDateInputValue(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}
